import { createSelector } from "reselect";

import { isRoundtrip } from "@app/helpers/search";
import { getSegmentIdsPerLeg } from "@app/helpers/udm/get-segment-ids-per-leg";
import { getPassengerSeats } from "@app/modules/checkout/helpers/seat-selection";
import { vehicleHasGenderedSeats } from "@app/modules/checkout/utils/bus";
import { getCartV2TripItem } from "@app/modules/checkout/utils/cart-v2";
import { isSeatSelectionAvailable } from "@app/modules/checkout/utils/departure";
import { DONE } from "@app/modules/results/constants/cart-status";
import { getSearch } from "@app/modules/results/selectors";
import { getCart } from "@app/modules/results/selectors/state-accessors";
import { getTripById } from "@app/modules/results/selectors/trips/get-trip";
import type { BusLayout } from "@app/types/napi-layout-types";

export const getCartTripItem = createSelector([getCart], cart_info => {
  if (!cart_info || !cart_info.cart) {
    return undefined;
  }

  const cart = cart_info.cart;
  return getCartV2TripItem(cart.items);
});

export const getSeatSelectionAvailability = createSelector(
  [getCartTripItem, getCart],
  (cart_trip_item, cart_info) => {
    if (!cart_trip_item) {
      return false;
    }

    return (
      cart_info?.status === DONE &&
      cart_trip_item.segments.some(segment => isSeatSelectionAvailable(segment))
    );
  }
);

export const firstSegmenthasGenderedSeats = createSelector(
  [getCartTripItem],
  cart_trip_item => {
    if (!cart_trip_item) {
      return false;
    }

    const first_segment = cart_trip_item.segments[0];

    return vehicleHasGenderedSeats(
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      first_segment.vehicle as unknown as BusLayout
    );
  }
);

export const getSeatSelectionPassengerSeats = createSelector(
  [
    getTripById,
    (_, __, passenger_index: number) => passenger_index,
    getCartTripItem,
    getSearch
  ],
  (trip, passenger_index, cart_trip_item, search) => {
    if (!cart_trip_item || !trip?.trip_legs) {
      return [];
    }

    const is_roundtrip = isRoundtrip(search);
    const segment_ids_per_leg_matrix = getSegmentIdsPerLeg(trip.trip_legs);

    // TODO-CART-V2-INTERCONNECTION: we need to handle all the ids for each leg, not only the first one
    const first_segment_id_per_leg = segment_ids_per_leg_matrix.map(
      leg => leg[0]
    );

    // It receives the index of the passenger and generate both inputs for outbound and return
    const passenger_per_index = cart_trip_item.passengers[passenger_index];

    if (!passenger_per_index) {
      return [];
    }

    return getPassengerSeats(
      passenger_per_index.selected_seats,
      passenger_index,
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      first_segment_id_per_leg,
      is_roundtrip,
      cart_trip_item.segments
    );
  }
);

// TODO async-cart: refactor common code with getSeatSelectionPassengerSeats above
// and add unit tests on all those selectors
export const getAllPassengerSeats = createSelector(
  [
    getTripById,
    (_, __, passengers: []) => passengers,
    getCartTripItem,
    getSearch
  ],
  (trip, passengers, cart_trip_item, search) => {
    if (!cart_trip_item || !trip?.trip_legs) {
      return [];
    }

    const is_roundtrip = isRoundtrip(search);
    const segment_ids_per_leg_matrix = getSegmentIdsPerLeg(trip.trip_legs);

    // TODO-CART-V2-INTERCONNECTION: we need to handle all the ids for each leg, not only the first one
    const first_segment_id_per_leg = segment_ids_per_leg_matrix.map(
      leg => leg[0]
    );

    return passengers.map((_p, passenger_index) => {
      // It receives the index of the passenger and generate both inputs for outbound and return
      const passenger_per_index = cart_trip_item.passengers[passenger_index];

      return getPassengerSeats(
        passenger_per_index ? passenger_per_index.selected_seats : null,
        passenger_index,
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        first_segment_id_per_leg,
        is_roundtrip,
        cart_trip_item.segments
      );
    });
  }
);
